// NPM MODULES
import React, { useState, useEffect, Fragment, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';

// NETWORKING
import useProviderReportRequests from '../../requests/ProviderReportRequests';

// FORMATTED MESSAGES
import ProviderReportsFormattedMessages from '../../messages/reports/ProviderReportsFormattedMessages';

import { ReportsRoutes } from '../../routes/ReportsRoutes';

// OUR COMPONENTS
// import SingleViewCustomer from './SingleViewCustomer';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import DataExportModal from '../../components/common/modals/DataExportModal';
import ProviderReportsTableRow from '../../components/reports/providers/ProviderReportsTableRow';
import ProviderReportsTable from '../../components/reports/providers/ProviderReportsTable';
import Layout from '../../components/layouts/Layout';
import Loader from '../../components/common/Loader';

const Provider = ({
  customer,
  providerData,
  updateActingProviderDetails,
  updateActingCustomerDetails,
  navigate,
  errorHandler,
  accessLevel,
  accessType,
}) => {
  const [smsList, setSmsList] = useState(null);
  const [data, setData] = useState([]);
  const [activeProviders, setActiveProviders] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [dataExists, setDataExists] = useState(false);
  const [totals, setTotals] = useState({
    Direct: 0,
    Standard: 0,
    Replies: 0,
    Sent: 0,
    Pending: 0,
    DirectParts: 0,
    StandardParts: 0,
    RepliesParts: 0,
    SentParts: 0,
    PendingParts: 0,
    Users: 0,
    Customers: 0,
  });
  const [selectedData, setSelectedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState('list');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [timeRange, setTimeRange] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const { getReportData, exportProviderReport } = useProviderReportRequests();

  useEffect(() => {
    if (customer && customer !== null) {
      if (providerData && providerData.length !== 0) {
        console.log('Have provider data:', providerData);
        setData(providerData.smslist);
        setDataExists(true);
        formatData(providerData.smslist);
      }
    }
  }, [customer, providerData]);

  const sortList = (data) => {
    return data.slice().sort((a, b) => {
      if (a.provider_name < b.provider_name) {
        return -1;
      }
      if (a.provider_name > b.provider_name) {
        return 1;
      }
      return 0;
    });
  };

  /*
    Formats the data in a manner required for the Table
    to properly display the data
  */
  const formatData = (data) => {
    try {
      let total = {
        Direct: 0,
        Standard: 0,
        Replies: 0,
        Sent: 0,
        Pending: 0,
        DirectParts: 0,
        StandardParts: 0,
        RepliesParts: 0,
        SentParts: 0,
        PendingParts: 0,
        Users: 0,
        Customers: 0,
      };

      const formattedData = data.map((el) => {
        total.Direct += parseInt(el.direct_sent, 10);
        total.Standard += parseInt(el.messages_sent, 10);
        total.Replies += parseInt(el.replies_sent, 10);
        total.Sent += parseInt(el.reminders_sent, 10);
        total.Pending += parseInt(el.reminders_pending, 10);
        total.DirectParts += parseInt(el.direct_parts, 10);
        total.StandardParts += parseInt(el.messages_parts, 10);
        total.RepliesParts += parseInt(el.replies_parts, 10);
        total.SentParts += parseInt(el.reminders_parts, 10);
        total.PendingParts += parseInt(el.reminders_pending_parts, 10);
        total.Users += parseInt(el.totals.users, 10);
        total.Customers += parseInt(el.totals.customers, 10);

        return (
          <ProviderReportsTableRow
            key={`item_${data.indexOf(el)}`}
            id={0}
            provider={el.provider_name}
            status={el.provider_status_name}
            customers={el.totals.customers}
            users={el.totals.users}
            Direct={{
              sent: el.direct_sent,
              parts: el.direct_parts,
            }}
            Standard={{
              sent: el.messages_sent,
              parts: el.messages_parts,
            }}
            Replies={{
              sent: el.replies_sent,
              parts: el.replies_parts,
            }}
            Sent={{
              sent: el.reminders_sent,
              parts: el.reminders_parts,
            }}
          />
        );
      });

      setSmsList(formattedData);
      setTotals(total);
      setLoading(false);
    } catch (e) {
      console.log('data error:', e);
    }
  };

  const backButton = () => {
    setActivePage('list');
  };

  /*
    Handles searching User Data between two dates
  */
  const handleProviderDataSearch = useCallback(async () => {
    if (fromDate && toDate) {
      setLoading(true);
      setDataExists(false);
      setData([]);
      setActiveProviders([]);
      setAllProviders([]);

      const searchParams = {
        fromDate: fromDate,
        toDate: toDate,
        timeRange: timeRange,
        timeRangeStart: fromDate,
        timeRangeEnd: toDate,
      };

      const result = await getReportData(searchParams, errorHandler);
      let filteredData = [];
      const activeProviders = result?.filter((e) => e.provider_status === 'A');

      if (showAll) {
        filteredData = result;
      } else {
        filteredData = activeProviders;
      }

      console.log('provider', result);

      setData(filteredData);
      setActiveProviders(activeProviders);
      setAllProviders(result);
      formatData(filteredData);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    handleProviderDataSearch();
  }, [handleProviderDataSearch]);

  const handleExportProviderReport = async () => {
    setExporting(true);
    const searchParams = {
      fromDate: fromDate,
      toDate: toDate,
    };

    const response = await exportProviderReport(searchParams, errorHandler);
    console.log('response', response);
    setShowEmailSentModal(true);
    setEmailRecipient(response);
    setExporting(false);
  };

  // HANDLERS START
  const changePage = (page, value) => {
    setActivePage(page);
    setSelectedData(value);
  };

  /*
    Generates the Search Tools for querying the data
  */
  const generateSearchTools = () => {
    return (
      <Box sx={{ marginBottom: { xs: 4, md: 2 } }}>
        <DateRangeSelector
          handleDateSearch={(fromDate, toDate, timeRange) =>
            handleProviderDataSearch(fromDate, toDate, timeRange)
          }
          parentStartDate={fromDate}
          setParentStartDate={setFromDate}
          parentEndDate={toDate}
          setParentEndDate={setToDate}
        />
      </Box>
    );
  };

  /*
    Convenience function to handle the updating of a form element
  */
  const handleInputChange = (event) => {
    const target = event.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    if (name === 'showAll') {
      setShowAll(value);

      if (value) {
        setData(allProviders);
        formatData(allProviders);
      } else {
        setData(activeProviders);
        formatData(activeProviders);
      }
    }
  };

  const handleShowAllCheckBox = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          marginBottom: 2,
        }}
      >
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                name="showAll"
                checked={showAll}
                onChange={(e) => handleInputChange(e)}
                sx={{ padding: 0.45 }}
              />
            }
            label="Show All"
            sx={{ margin: 0 }}
          />
        </FormGroup>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleExportProviderReport}
          disabled={exporting}
        >
          Email Report
        </Button>
      </Box>
    );
  };

  if (accessLevel === '3' && accessType === '3') {
    return (
      <Layout
        title={ProviderReportsFormattedMessages.providerReportsTitle}
        routes={ReportsRoutes}
        accessLevel={accessLevel}
        accessType={accessType}
      >
        {generateSearchTools()}
        {loading ? (
          <Loader />
        ) : (
          <ProviderReportsTable
            changePage={changePage}
            totals={totals}
            list={smsList}
            loading={loading}
            checkBox={handleShowAllCheckBox}
          />
        )}
        <DataExportModal
          show={showEmailSentModal}
          onHide={() => setShowEmailSentModal(false)}
          emailRecipient={emailRecipient}
        />
      </Layout>
    );
  }
  return null;
};

export default Provider;
